import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../../../components/ui/dialog';
import { Button } from '../../../../../components/ui/button';
import { Label } from '../../../../../components/ui/label';
import { Input } from '../../../../../components/ui/input';
import { FileUpload } from '../../../../../components/ui/FileUpload';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../components/ui/select';
import { Plus, Trash2 } from 'lucide-react';
import type { DashboardProject } from '../../../types/project';
import { DocumentTag } from '../../../types';
import { getPreSignedUrlToUpload, bulkCreateUpdateInspectionPersonnel, getAllInspectionPersonnel } from '../../../../../redux/Project/registrySaga';
import { Notification as customNotification } from '../../../../../components/ui/custom-notification';
import AddNewItemDialog from '../../../../../components/ui/AddNewItemDialog';
import { getListMaterialCategory, AddMaterialCategory } from '../../../../../redux/Project/materialSaga';
import { AntdDate } from '../../../../../components/ui/antd-date';
import { SearchableSelect } from '../../../../../components/ui/SearchableSelect';
import { useTeamMembersReduxHook } from '../../../hooks/useTeamMembersRedux';
import { refreshQCInspectionPersonnelData } from '../../../../../redux/Project/registryData/action';

interface AddQCInspectionPersonnelModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: () => void;
  project: DashboardProject;
  personnelToEdit?: any;
  existingPersonnel?: any[];
}

interface Qualification {
  qualification_type: string;
  level_type: string;
  certification_number?: string;
  qualification_id?: string;
  expiration_date: string;
}

const AddQCInspectionPersonnelModal: React.FC<AddQCInspectionPersonnelModalProps> = ({
  isOpen,
  onClose,
  onAdd,
  project,
  personnelToEdit,
  existingPersonnel
}) => {
  const dispatch = useDispatch();
  const isEditMode = !!personnelToEdit;
  const [operatorName, setOperatorName] = useState('');
  const [operatorUserId, setOperatorUserId] = useState<number | null>(null);
  const [certificationNumber, setCertificationNumber] = useState('');
  const [eyesightExaminationPassed, setEyesightExaminationPassed] = useState<boolean | null>(false);
  // Get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  const [eyesightExaminationExpiryDate, setEyesightExaminationExpiryDate] = useState<string>(getTodayDate());
  const [qualifications, setQualifications] = useState<Qualification[]>([]);
  const [qualificationTypes, setQualificationTypes] = useState<any[]>([]);
  const [isAddingQualificationType, setIsAddingQualificationType] = useState(false);
  const [personnelRole, setPersonnelRole] = useState<string>('');
  const [personnelRoles, setPersonnelRoles] = useState<any[]>([]);
  const [isAddingPersonnelRole, setIsAddingPersonnelRole] = useState(false);
  const [isAddingOperatorName, setIsAddingOperatorName] = useState(false);
  const [customOperatorNames, setCustomOperatorNames] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [materialDocuments, setMaterialDocuments] = useState<File[]>([]);
  const [selectedTags, setSelectedTags] = useState<any[]>([]);
  const [isAddingTag, setIsAddingTag] = useState(false);
  const [documentTags, setDocumentTags] = useState<DocumentTag[]>([]);

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  // Use Redux hook to fetch team members
  const {
    teamMembers,
    loading: teamMembersLoading
  } = useTeamMembersReduxHook(undefined, Number(project.project.projectId));

  // Filter available team members (excluding those already in personnel list)
  const availableTeamMembers = useMemo(() => {
    if (!teamMembers || teamMembers.length === 0) return [];
    
    return teamMembers.filter((member: any) => {
      // Get user info from team member
      const user = member.apiData?.user || member.user;
      if (!user) return false;
      
      const fullName = `${user.first_name} ${user.last_name}`.trim();
      
      // Filter out team members already in existing personnel (excluding current edit)
      if (existingPersonnel && existingPersonnel.some((personnel: any) => {
        // Exclude current personnel if editing
        if (isEditMode && personnelToEdit?.id && personnel.id === personnelToEdit.id) {
          return false;
        }
        return personnel.operator_name === fullName;
      })) {
        return false;
      }
      
      return true;
    });
  }, [teamMembers, existingPersonnel, isEditMode, personnelToEdit?.id]);

  // Create combined options for operator names (team members + default + custom names)
  const operatorNameOptions = useMemo(() => {
   

    // Team member options
    const teamMemberOptions = availableTeamMembers
      .map((member: any) => {
        const user = member.apiData?.user || member.user;
        if (!user) return null;
        const fullName = `${user.first_name} ${user.last_name}`.trim();
        return {
          id: `team-${user.user_id || member.apiData?.user_id || member.userId}`,
          name: fullName
        };
      })
      .filter((option: { id: string; name: string } | null): option is { id: string; name: string } => option !== null);

    // Custom operator name options
    const customOptions = customOperatorNames.map((name, index) => ({
      id: `custom-${index}`,
      name: name
    }));

    // Combine all options, removing duplicates by name
    const allOptions = [...teamMemberOptions, ...customOptions];
    const existingNames = new Set(allOptions.map(opt => opt.name.toLowerCase()));
    
    // defaultOptions.forEach(defaultOpt => {
    //   if (!existingNames.has(defaultOpt.name.toLowerCase())) {
    //     allOptions.push(defaultOpt);
    //   }
    // });

    return allOptions;
  }, [availableTeamMembers, customOperatorNames]);

  // Load qualification types, personnel roles and document tags on mount
  useEffect(() => {
    const loadTags = async () => {
      try {
        const tags = await getListMaterialCategory();
        if (tags && tags.length > 0) {
          const qcQualificationTags = tags.filter((tag: any) => 
            tag.tags_type === 'QUALIFICATION_TYPE' ||
            tag.tags_type === 'QC_QUALIFICATION_TYPE' ||
            tag.tags_type?.toLowerCase().includes('qualification')
          );
          setQualificationTypes(qcQualificationTags);

          const personnelRoleTags = tags.filter((tag: any) => 
            tag.tags_type === 'PERSONNEL_ROLE' ||
            tag.tags_type === 'QC_PERSONNEL_ROLE' ||
            tag.tags_type?.toLowerCase().includes('personnel_role')
          );
          setPersonnelRoles(personnelRoleTags);

          const personnelDocTags = tags.filter((tag: any) => 
            tag.tags_type === 'EQUIPMENT_AND_CONSUMABLE_DOCUMENT_TAGS' ||
            tag.tags_type === 'INSPECTION_PERSONNEL_DOCUMENT_TAGS'
          );
          setDocumentTags(personnelDocTags);
        }
      } catch (error) {
        console.error('Failed to load tags:', error);
      }
    };

    if (isOpen) {
      loadTags();
    }
  }, [isOpen]);

  // Initialize state when dialog opens
  useEffect(() => {
    if (isOpen) {
      if (personnelToEdit) {
        // Edit mode - populate with existing data
        setOperatorName(personnelToEdit.operator_name || '');
        setOperatorUserId(personnelToEdit.operator_user_id || personnelToEdit.operatorUserId || null);
        setPersonnelRole(personnelToEdit.personnel_role || personnelToEdit.personnelRole || '');
        setCertificationNumber(personnelToEdit.certification_number || '');
        setEyesightExaminationPassed(
          personnelToEdit.eyesight_examination_passed !== undefined 
            ? personnelToEdit.eyesight_examination_passed 
            : false
        );
        setEyesightExaminationExpiryDate(personnelToEdit.eyesight_examination_expiry_date || getTodayDate());
        setQualifications(personnelToEdit.qualifications || []);
        setMaterialDocuments([]);
        setSelectedTags([]);
      } else {
        // Add mode - reset to defaults
        setOperatorName('');
        setOperatorUserId(null);
        setPersonnelRole('');
        setCertificationNumber('');
        setEyesightExaminationPassed(false); // Default to No
        setEyesightExaminationExpiryDate(getTodayDate()); // Default to today's date
        setQualifications([{
          qualification_type: '',
          level_type: '',
          certification_number: '',
          expiration_date: ''
        }]);
        setMaterialDocuments([]);
        setSelectedTags([]);
      }
    }
  }, [isOpen, personnelToEdit]);

  // Convert document tags to options for the MultiSelect component
  const tagOptions = useMemo(() =>
    documentTags.map(tag => ({
      value: tag.tags_id.toString(),
      label: tag.tags_name
    })), [documentTags]
  );

  const handleMaterialFileSelect = (files: FileList | null) => {
    if (files && files.length > 0) {
      const fileArray = Array.from(files);
      setMaterialDocuments(prev => [...prev, ...fileArray]);
      // Initialize tags array for new files
      const newTags = Array(fileArray.length).fill([]);
      setSelectedTags(prev => [...prev, ...newTags]);
    }
  };

  const handleRemoveFile = (index: number) => {
    setMaterialDocuments(prev => prev.filter((_, i) => i !== index));
    setSelectedTags(prev => prev.filter((_, i) => i !== index));
  };

  const handleTagsChange = (fileIndex: number, tags: string[]) => {
    const updatedTags = [...selectedTags];
    updatedTags[fileIndex] = tags;
    setSelectedTags(updatedTags);
  };

  const handleAddTag = async (tagName: string) => {
    if (tagName.trim()) {
      const existingTag = documentTags.find((tag: any) => tag.tags_name.toLowerCase() === tagName.trim().toLowerCase());
      if (existingTag) {
        customNotification.warning(`${tagName.trim()} tag already exists`);
        return;
      }

      const apiPayload = {
        tags_name: tagName.trim(),
        project_id: Number(project.project.projectId),
        tags_type: 'EQUIPMENT_AND_CONSUMABLE_DOCUMENT_TAGS',
        created_by: user.email || ''
      };
      const res = await AddMaterialCategory(apiPayload, 'Document Tag');

      if (res) {
        const updatedTags = [...documentTags, res];
        setDocumentTags(updatedTags);
        setIsAddingTag(false);
      }
    }
  };

  // Convert qualification types to options for SearchableSelect (like modelOptions)
  const qualificationTypeOptions = useMemo(() => {
    // Default options based on user's examples
    const defaultOptions = [
      { id: 'welding-inspection', name: 'Welding Inspection' },
      { id: 'aut', name: 'AUT' },
      { id: 'ultrasonic', name: 'Ultrasonic' },
      { id: 'magnetic-particle', name: 'Magnetic Particle' },
      { id: 'radiographic', name: 'Radiographic' },
      { id: 'visual-inspection', name: 'Visual Inspection' },
      { id: 'liquid-penetrant', name: 'Liquid Penetrant' },
      { id: 'eddy-current', name: 'Eddy Current' }
    ];

    // Convert database tags to SearchableSelect format
    const dbOptions = qualificationTypes.map((tag: any) => ({
      id: tag.tags_id?.toString() || '',
      name: tag.tags_name || tag.name || ''
    })).filter(opt => opt.id && opt.name);

    // Combine database options with defaults, removing duplicates by name
    const allOptions = [...dbOptions];
    const dbNames = new Set(dbOptions.map(opt => opt.name.toLowerCase()));
    
    defaultOptions.forEach(defaultOpt => {
      if (!dbNames.has(defaultOpt.name.toLowerCase())) {
        allOptions.push(defaultOpt);
      }
    });

    // If database has entries, return combined; otherwise return defaults only
    return allOptions.length > 0 ? allOptions : defaultOptions;
  }, [qualificationTypes]);

  // Convert personnel roles to options for SearchableSelect
  const personnelRoleOptions = useMemo(() => {
    // Default options for personnel roles
    const defaultOptions = [
      { id: 'inspector', name: 'Inspector' },
      { id: 'senior-inspector', name: 'Senior Inspector' },
      { id: 'lead-inspector', name: 'Lead Inspector' },
      { id: 'supervisor', name: 'Supervisor' },
      { id: 'technician', name: 'Technician' }
    ];

    // Convert database tags to SearchableSelect format
    const dbOptions = personnelRoles.map((tag: any) => ({
      id: tag.tags_id?.toString() || '',
      name: tag.tags_name || tag.name || ''
    })).filter(opt => opt.id && opt.name);

    // Combine database options with defaults, removing duplicates by name
    const allOptions = [...dbOptions];
    const dbNames = new Set(dbOptions.map(opt => opt.name.toLowerCase()));
    
    defaultOptions.forEach(defaultOpt => {
      if (!dbNames.has(defaultOpt.name.toLowerCase())) {
        allOptions.push(defaultOpt);
      }
    });

    // If database has entries, return combined; otherwise return defaults only
    return allOptions.length > 0 ? allOptions : defaultOptions;
  }, [personnelRoles]);

  const handleAddQualification = () => {
    setQualifications([...qualifications, {
      qualification_type: '',
      level_type: '',
      certification_number: '',
      expiration_date: ''
    }]);
  };

  const handleRemoveQualification = (index: number) => {
    if (qualifications.length > 1) {
      setQualifications(qualifications.filter((_, i) => i !== index));
    } else {
      customNotification.warning('At least one qualification is required');
    }
  };

  const handleQualificationChange = (index: number, field: keyof Qualification, value: string) => {
    const updated = [...qualifications];
    updated[index] = {
      ...updated[index],
      [field]: value
    };
    setQualifications(updated);
  };

  const handleAddQualificationType = async (typeName: string) => {
    if (typeName.trim()) {
      // Check if it already exists in current options
      if (qualificationTypeOptions.find((opt: any) => opt.name.toLowerCase() === typeName.trim().toLowerCase())) {
        customNotification.warning(`${typeName.trim()} qualification type already exists`);
        return;
      }

      const apiPayload = {
        tags_name: typeName.trim(),
        project_id: Number(project.project.projectId),
        tags_type: 'QUALIFICATION_TYPE',
        created_by: user.email || ''
      };
      const res = await AddMaterialCategory(apiPayload, 'Qualification Type');

      if (res) {
        // Reload qualification types from database
        try {
          const tags = await getListMaterialCategory();
          if (tags && tags.length > 0) {
            const qcQualificationTags = tags.filter((tag: any) => 
              tag.tags_type === 'QUALIFICATION_TYPE' ||
              tag.tags_type === 'QC_QUALIFICATION_TYPE' ||
              tag.tags_type?.toLowerCase().includes('qualification')
            );
            setQualificationTypes(qcQualificationTags);
          }
        } catch (error) {
          console.error('Failed to reload qualification types:', error);
          // Fallback: add to current list
          const updatedTypes = [...qualificationTypes, res];
          setQualificationTypes(updatedTypes);
        }
        setIsAddingQualificationType(false);
      }
    }
  };

  const handleAddPersonnelRole = async (roleName: string) => {
    if (roleName.trim()) {
      // Check if it already exists in current options
      if (personnelRoleOptions.find((opt: any) => opt.name.toLowerCase() === roleName.trim().toLowerCase())) {
        customNotification.warning(`${roleName.trim()} personnel role already exists`);
        return;
      }

      const apiPayload = {
        tags_name: roleName.trim(),
        project_id: Number(project.project.projectId),
        tags_type: 'PERSONNEL_ROLE',
        created_by: user.email || ''
      };
      const res = await AddMaterialCategory(apiPayload, 'Personnel Role');

      if (res) {
        // Reload personnel roles from database
        try {
          const tags = await getListMaterialCategory();
          if (tags && tags.length > 0) {
            const personnelRoleTags = tags.filter((tag: any) => 
              tag.tags_type === 'PERSONNEL_ROLE' ||
              tag.tags_type === 'QC_PERSONNEL_ROLE' ||
              tag.tags_type?.toLowerCase().includes('personnel_role')
            );
            setPersonnelRoles(personnelRoleTags);
          }
        } catch (error) {
          console.error('Failed to reload personnel roles:', error);
          // Fallback: add to current list
          const updatedRoles = [...personnelRoles, res];
          setPersonnelRoles(updatedRoles);
        }
        setIsAddingPersonnelRole(false);
      }
    }
  };

  const handleAddOperatorName = (operatorName: string) => {
    if (operatorName.trim()) {
      // Check if it already exists
      const nameLower = operatorName.trim().toLowerCase();
      const existsInTeamMembers = availableTeamMembers.some((member: any) => {
        const user = member.apiData?.user || member.user;
        if (!user) return false;
        const fullName = `${user.first_name} ${user.last_name}`.trim();
        return fullName.toLowerCase() === nameLower;
      });
      
      const existsInCustom = customOperatorNames.some(name => name.toLowerCase() === nameLower);
      
      if (existsInTeamMembers || existsInCustom) {
        customNotification.warning(`${operatorName.trim()} operator name already exists`);
        return;
      }

      // Add to custom operator names list
      setCustomOperatorNames(prev => [...prev, operatorName.trim()]);
      // Set as current operator name
      setOperatorName(operatorName.trim());
      setOperatorUserId(null);
      setIsAddingOperatorName(false);
      customNotification.success('Operator name added successfully');
    }
  };

  const handleClose = () => {
    setOperatorName('');
    setOperatorUserId(null);
    setPersonnelRole('');
    setCertificationNumber('');
    setEyesightExaminationPassed(false); // Reset to default No
    setEyesightExaminationExpiryDate(getTodayDate()); // Reset to today's date
    setQualifications([{
      qualification_type: '',
      level_type: '',
      certification_number: '',
      expiration_date: ''
    }]);
    setMaterialDocuments([]);
    setSelectedTags([]);
    setIsUploading(false);
    // Note: We keep customOperatorNames in state so they persist during the session
    onClose();
  };

  const handleSubmit = async () => {
    // Validate required fields
    if (!operatorName?.trim()) {
      customNotification.warning('Please enter or select an operator name');
      return;
    }

    // Validate personnel role
    if (!personnelRole?.trim()) {
      customNotification.warning('Please select a personnel role');
      return;
    }

    // Validate qualifications
    const hasValidQualification = qualifications.some(q => 
      q.qualification_type?.trim() && 
      (q.certification_number?.trim() || q.qualification_id?.trim())
    );

    if (!hasValidQualification) {
      customNotification.warning('Please add at least one qualification with qualification type and certification/qualification ID');
      return;
    }

    setIsUploading(true);
    try {
      let documents: any[] = [];

      // If documents are uploaded, upload them to S3 first
      if (materialDocuments.length > 0) {
        const payload = {
          pipeId: 0,
          projectId: Number(project.project.projectId),
          projectTypeId: 1,
          type: 'INSPECTION_PERSONNEL'
        };

        const s3Response = await getPreSignedUrlToUpload(materialDocuments, payload, 1);

        if (s3Response && s3Response.length > 0) {
          documents = s3Response.map((file: any, index: number) => ({
            fileName: file.fileName,
            s3Key: file.s3Key,
            documentTags: selectedTags[index] || [],
            uploadedBy: user.email || '',
            docRef: index + 1
          }));
        }
      }

      const personnelItem: any = {
        project_id: Number(project.project.projectId),
        operator_name: operatorName.trim(),
        operator_user_id: operatorUserId,
        personnel_role: personnelRole?.trim() || null,
        certification_number: certificationNumber?.trim() || null,
        eyesight_examination_passed: eyesightExaminationPassed,
        eyesight_examination_expiry_date: eyesightExaminationExpiryDate || null,
        qualifications: qualifications
          .filter(q => q.qualification_type?.trim()) // Only include qualifications with type
          .map(q => ({
            qualification_type: q.qualification_type?.trim() || null,
            level_type: q.level_type?.trim() || null,
            certification_number: q.certification_number?.trim() || null,
            qualification_id: q.qualification_id?.trim() || null,
            expiration_date: q.expiration_date || null
          })),
        documents: documents.length > 0 ? documents : [],
        created_by: user.email || null
      };

      // If editing, include the id for update
      if (isEditMode && personnelToEdit?.id) {
        personnelItem.id = personnelToEdit.id;
      }

      const personnelData = {
        items: [personnelItem]
      };

      // Call API to create/update personnel
      const result = await bulkCreateUpdateInspectionPersonnel(personnelData);

      if (result) {
        if (isEditMode) {
          customNotification.success(
            'Personnel updated successfully',
            'The QC Inspection Personnel has been updated successfully.'
          );
        } else {
          customNotification.success(
            'Personnel added successfully',
            'The QC Inspection Personnel has been added successfully.'
          );
        }
        // Refresh Redux store data to update the personnel list
        dispatch(refreshQCInspectionPersonnelData({ projectId: project.project.projectId }));
        onAdd();
        handleClose();
      }
    } catch (error) {
      customNotification.error(
        'Failed to save personnel',
        'There was an error saving the personnel. Please try again.'
      );
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="w-[90vw] max-w-[1300px]">
          <DialogHeader className="flex flex-row items-center justify-between gap-3 pr-14">
            <DialogTitle className="text-[18px] font-semibold text-blueprint-blue leading-tight">
              {isEditMode ? 'Edit' : 'Add'} QC Inspection Personnel
            </DialogTitle>
            <Button
              onClick={handleSubmit}
              disabled={isUploading || !operatorName.trim() || !personnelRole.trim()}
              size="sm"
              className="h-9 px-4 bg-hero-red text-white hover:bg-hero-red-60"
            >
              {isUploading ? 'Saving...' : isEditMode ? 'Update Personnel' : 'Add Personnel'}
            </Button>
          </DialogHeader>

          <div className="grid p-2 grid-cols-2 gap-4 min-w-0 max-h-[75vh] flex flex-col overflow-y-auto overflow-x-hidden">
            {/* Operator Name */}
            <div className="space-y-2">
              <Label htmlFor="operatorName" className="text-[12px] font-medium text-text-grey">Operator Name <span className="text-hero-red">*</span></Label>
              <div className="relative mt-1">
                {isEditMode && personnelToEdit?.operator_name ? (
                  // Edit mode: Show disabled name field with current operator info
                  <Input
                    id="operatorName"
                    value={operatorName}
                    className="bg-page-bg text-text-grey cursor-not-allowed"
                    disabled={true}
                  />
                ) : (
                  // Add mode: Show user selection dropdown
                  <SearchableSelect
                    value={operatorName}
                    onValueChange={(value: string) => {
                      // Check if it's a team member
                      const selectedMember = availableTeamMembers.find((member: any) => {
                        const user = member.apiData?.user || member.user;
                        if (!user) return false;
                        const fullName = `${user.first_name} ${user.last_name}`.trim();
                        return fullName === value;
                      });
                      
                      if (selectedMember) {
                        const user = selectedMember.apiData?.user || selectedMember.user;
                        if (user) {
                          const fullName = `${user.first_name} ${user.last_name}`.trim();
                          setOperatorName(fullName);
                          setOperatorUserId(user.user_id || selectedMember.apiData?.user_id || selectedMember.userId);
                        }
                      } else {
                        // Custom operator name or default option
                        setOperatorName(value);
                        setOperatorUserId(null);
                      }
                    }}
                    placeholder="Select operator name or add new..."
                    searchPlaceholder="Search operator name..."
                    options={operatorNameOptions}
                    showAddButton={true}
                    onAddNew={() => setIsAddingOperatorName(true)}
                    addNewText="Add New Operator Name"
                    disabled={teamMembersLoading}
                    loading={teamMembersLoading}
                    loadingText="Loading team members..."
                  />
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[12px] font-medium text-text-grey">Personnel Role <span className="text-hero-red">*</span></Label>

              <div className="relative mt-1">
                <SearchableSelect
                  value={personnelRole}
                  onValueChange={(value: string) => {
                    setPersonnelRole(value);
                  }}
                  placeholder="Select personnel role"
                  searchPlaceholder="Search personnel roles..."
                  options={personnelRoleOptions}
                  onAddNew={() => setIsAddingPersonnelRole(true)}
                  addNewText="Add New Personnel Role"
                />
              </div>
            </div>
            {/* Eyesight Certification Number */}
            <div className="space-y-2">
              <Label htmlFor="certificationNumber" className="text-[12px] font-medium text-text-grey">Certification Number</Label>
              <Input
                id="certificationNumber"
                value={certificationNumber}
                onChange={(e) => setCertificationNumber(e.target.value)}
                placeholder="Enter certification number"
                className="w-full"
              />
            </div>

            {/* Eyesight Examination Passed - Yes/No Toggle */}
            <div className="space-y-2">
              <Label className="text-[12px] font-medium text-text-grey">Eyesight Examination Passed</Label>
              <div className="flex items-center gap-2 mt-2">
                <Button
                  type="button"
                  variant={eyesightExaminationPassed === true ? "default" : "outline"}
                  onClick={() => setEyesightExaminationPassed(true)}
                  className={eyesightExaminationPassed === true ? "bg-hero-red text-white hover:bg-hero-red-60" : ""}
                >
                  Yes
                </Button>
                <Button
                  type="button"
                  variant={eyesightExaminationPassed === false ? "default" : "outline"}
                  onClick={() => setEyesightExaminationPassed(false)}
                  className={eyesightExaminationPassed === false ? "bg-hero-red text-white hover:bg-hero-red-60" : ""}
                >
                  No
                </Button>
              </div>
            </div>

            {/* Eyesight Examination Expiry Date */}
            <div className="space-y-2">
              <Label className="text-[12px] font-medium text-text-grey">Eyesight Examination Expiry Date</Label>
              <AntdDate
                name="eyesightExpiryDate"
                value={eyesightExaminationExpiryDate}
                onChange={(value: string) => {
                  setEyesightExaminationExpiryDate(value);
                }}
                className="w-full"
              />
            </div>

            {/* Qualifications Section */}
            <div className="space-y-2 col-span-2">
              <div className="flex items-center justify-between">
                <Label className="font-heading text-[14px] font-semibold text-blueprint-blue">Qualifications</Label>
                <Button
                  type="button"
                  onClick={handleAddQualification}
                  size="sm"
                  variant="outline"
                  className="flex items-center gap-2 border-[#E2E5EA] text-text-grey hover:bg-page-bg"
                >
                  <Plus className="h-4 w-4" />
                  Add Qualification
                </Button>
              </div>

              <div className="space-y-4 border border-[#E2E5EA] rounded-lg p-4">
                {qualifications.map((qualification, index) => (
                  <div key={index} className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-4 p-4 border border-[#E2E5EA] rounded-lg bg-page-bg">
                    <div className="space-y-2">
                      <Label className="text-[12px] font-medium text-text-grey">Qualification Type <span className="text-hero-red">*</span></Label>

                      <div className="relative mt-1">
                        <SearchableSelect
                          value={qualification.qualification_type}
                          onValueChange={(value: string) => {
                            handleQualificationChange(index, 'qualification_type', value);
                          }}
                          placeholder="Select qualification type"
                          searchPlaceholder="Search qualification types..."
                          options={qualificationTypeOptions}
                          onAddNew={() => setIsAddingQualificationType(true)}
                          addNewText="Add New Qualification Type"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-[12px] font-medium text-text-grey">Level Type</Label>
                      <Input
                        value={qualification.level_type}
                        onChange={(e) =>
                          handleQualificationChange(index, 'level_type', e.target.value)
                        }
                        placeholder="e.g., CWI, Level 2-AUT-02A/02B (SCR), PCN Level 2D"
                        className="w-full"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-[12px] font-medium text-text-grey">Certification Number / Qualification ID</Label>
                      <Input
                        value={qualification.certification_number || qualification.qualification_id || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          handleQualificationChange(index, 'certification_number', value);
                          handleQualificationChange(index, 'qualification_id', value);
                        }}
                        placeholder="Enter certification number or qualification ID"
                        className="w-full"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-[12px] font-medium text-text-grey">Expiration Date</Label>
                      <AntdDate
                        name={`qualificationExpiry-${index}`}
                        value={qualification.expiration_date}
                        onChange={(value: string) => 
                          handleQualificationChange(index, 'expiration_date', value)
                        }
                        className="w-full"
                      />
                    </div>

                    {qualifications.length > 1 && (
                      <Button
                        type="button"
                        onClick={() => handleRemoveQualification(index)}
                        size="sm"
                        variant="outline"
                        className="h-9 w-9 p-0 self-end text-hero-red hover:text-hero-red hover:bg-[rgba(235,28,55,0.08)] border-[#E2E5EA]"
                        title="Remove qualification"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Documents Upload Section */}
            <div className="space-y-2 col-span-2">
              <Label className="text-[12px] font-medium text-text-grey">Upload Documents</Label>
              <p className="text-[12px] text-heritage-grey">
                Upload QC Inspection Personnel documents.
                <br />
                <span className="text-[11px] text-heritage-grey">
                  Allowed: PDF, Word (.doc, .docx), Excel (.xls, .xlsx), Text (.txt), Images (.jpg, .png, .gif)
                  <br />
                  Maximum file size: 100MB per file
                </span>
              </p>

              <FileUpload
                onFileSelect={handleMaterialFileSelect}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png,.gif"
                multiple={true}
                maxFiles={100}
                buttonText="Upload Documents"
                variant="outline"
                size="lg"
                onRemoveFile={handleRemoveFile}
                selectedFiles={materialDocuments}
                showFileList={materialDocuments.length > 0}
                className="w-full"
                showTags={true}
                optionsTags={tagOptions}
                selectedTags={selectedTags}
                showAddButton={true}
                addButtonText="Add New Tag"
                onAddButtonClick={() => setIsAddingTag(true)}
                onTagsChange={handleTagsChange}
                maxHeight=""
              />

              <div className="text-[11px] text-heritage-grey">
                Current files: {materialDocuments.length} file(s)
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add New Qualification Type Dialog */}
      <AddNewItemDialog
        open={isAddingQualificationType}
        onOpenChange={setIsAddingQualificationType}
        title="Add New Qualification Type"
        label="Qualification Type Name"
        placeholder="Enter qualification type (e.g., AUT, Ultrasonic, Magnetic Particle)"
        onAdd={handleAddQualificationType}
        buttonText="Add Qualification Type"
        cancelText="Cancel"
      />

      {/* Add New Personnel Role Dialog */}
      <AddNewItemDialog
        open={isAddingPersonnelRole}
        onOpenChange={setIsAddingPersonnelRole}
        title="Add New Personnel Role"
        label="Personnel Role Name"
        placeholder="Enter personnel role (e.g., Inspector, Senior Inspector, Lead Inspector)"
        onAdd={handleAddPersonnelRole}
        buttonText="Add Personnel Role"
        cancelText="Cancel"
      />

      {/* Add New Operator Name Dialog */}
      <AddNewItemDialog
        open={isAddingOperatorName}
        onOpenChange={setIsAddingOperatorName}
        title="Add New Operator Name"
        label="Operator Name"
        placeholder="Enter operator name (e.g., John Doe, Jane Smith)"
        onAdd={handleAddOperatorName}
        buttonText="Add Operator Name"
        cancelText="Cancel"
      />

      {/* Add New Tag Dialog */}
      <AddNewItemDialog
        open={isAddingTag}
        onOpenChange={setIsAddingTag}
        title="Add New Tag"
        label="Tag Name"
        placeholder="Enter tag name"
        onAdd={handleAddTag}
        buttonText="Add Tag"
        cancelText="Cancel"
      />
    </>
  );
};

export { AddQCInspectionPersonnelModal };
